/**
 * Web device capability detection using Browser APIs
 * ⚠️ Update test-console.js when modifying this file
 */

import { deviceCapabilitiesConfig } from './config';
import { DevicePerformanceLevel } from './consts';

// Fallback values for unavailable WebGL parameters
const FALLBACK_MIN_TEXTURE_SIZE = 4096;
const FALLBACK_MIN_VERTEX_UNIFORM_VECTORS = 1024;
const FALLBACK_DEFAULT_CPU_CORES = 2;
const FALLBACK_GPU_VALUE = 0;

/** GPU capabilities via WebGL API */
interface GPUCapabilities {
  maxTextureSize: number;
  maxVertexUniformVectors: number;
  renderer: string | null;
  vendor: string | null;
}

/** Battery information from Battery Status API */
interface BatteryInfo {
  level: number | null; // percentage (0-100)
  charging: boolean | null;
  supported: boolean;
}

/** Storage information from Storage API */
interface StorageInfo {
  quota: number | null; // GB
  usage: number | null; // GB
  supported: boolean;
}

/** CPU benchmark result */
interface BenchmarkResult {
  isSlow: boolean;
  duration: number | null; // milliseconds
}

/** Complete device capability snapshot */
interface DeviceCapabilities {
  cpuCores: number;
  deviceMemory: number | null;
  isMemoryCapped: boolean; // true if memory is capped at 8GB (browser fingerprinting protection)
  gpu: GPUCapabilities | null;
  platform: string | null;
  userAgent: string | null;
  maxTouchPoints: number;
  vendor: string | null;
  onLine: boolean;
  connectionType: string | null;
  connectionEffectiveType: string | null;
  connectionDownlink: number | null;
  connectionRtt: number | null;
  battery: BatteryInfo;
  storage: StorageInfo;
  refreshRate: number | null; // Hz
  slowDevice: BenchmarkResult;
}

/** Software renderer patterns to filter out (no actual GPU) */
const SOFTWARE_RENDERER_PATTERNS = [
  'microsoft basic render driver',
  'software renderer',
  'llvmpipe',
  'mesa llvmpipe',
  'swiftshader',
  'software rasterizer',
  'chromium software renderer',
  'software rendering',
  'cpu',
];

/** Detects Windows platform */
function isWindows(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const platform = navigator.platform?.toLowerCase() || '';
  const userAgent = navigator.userAgent?.toLowerCase() || '';
  return platform.includes('win') || userAgent.includes('windows');
}

/** Detects Apple Silicon (M1/M2/M3) - no hyperthreading, hardwareConcurrency = physical cores */
function isAppleSilicon(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const platform = navigator.platform?.toLowerCase() || '';
  const userAgent = navigator.userAgent?.toLowerCase() || '';
  const vendor = navigator.vendor?.toLowerCase() || '';

  const isMac = platform.includes('mac') || userAgent.includes('mac os');
  const isApple = vendor.includes('apple') || userAgent.includes('apple');
  const isARM =
    userAgent.includes('arm') ||
    userAgent.includes('aarch64') ||
    userAgent.includes('apple m') ||
    userAgent.includes('apple silicon');
  const isIntelMac =
    userAgent.includes('intel') ||
    userAgent.includes('x86_64') ||
    userAgent.includes('x86-64') ||
    userAgent.includes('intel mac');
  if (isMac && isApple) {
    if (isARM) return true;
    if (isIntelMac) return false;
    return true; // Modern Macs without Intel indicators are Apple Silicon
  }
  return false;
}

/** Estimates physical CPU cores from logical cores (handles hyperthreading) */
function estimatePhysicalCores(logicalCores: number): number {
  if (isAppleSilicon()) return logicalCores;
  if (logicalCores <= 2) return logicalCores;
  if (logicalCores % 2 === 1) return logicalCores; // Odd = physical (HT doubles cores)
  if (isWindows()) return Math.floor(logicalCores / 2); // Windows commonly uses HT
  if (logicalCores >= 16 && logicalCores % 4 === 0) return Math.floor(logicalCores / 2);
  return logicalCores;
}

/** Detects GPU via WebGL API, filters software renderers */
function detectGPU(): GPUCapabilities | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl') ||
      canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;

    if (!gl) {
      return null;
    }

    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE);
    const maxVertexUniformVectors = gl.getParameter(gl.MAX_VERTEX_UNIFORM_VECTORS);

    const renderer = debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : null;
    const vendor = debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : null;

    if (renderer) {
      const rendererLower = renderer.toLowerCase();
      const isSoftwareRenderer = SOFTWARE_RENDERER_PATTERNS.some((pattern) =>
        rendererLower.includes(pattern),
      );
      const isLikelySoftware = (maxTextureSize ?? 0) < 2048 || (maxVertexUniformVectors ?? 0) < 256;
      if (isSoftwareRenderer || isLikelySoftware) return null;
    }

    return {
      maxTextureSize: maxTextureSize ?? FALLBACK_GPU_VALUE,
      maxVertexUniformVectors: maxVertexUniformVectors ?? FALLBACK_GPU_VALUE,
      renderer: renderer ?? null,
      vendor: vendor ?? null,
    };
  } catch {
    return null;
  }
}

/** Retrieves network connection info, handles vendor prefixes */
function getConnectionInfo(): {
  type: string | null;
  effectiveType: string | null;
  downlink: number | null;
  rtt: number | null;
} {
  if (typeof navigator === 'undefined') {
    return { type: null, effectiveType: null, downlink: null, rtt: null };
  }

  const navigatorWithConnection = navigator as unknown as {
    connection?: {
      type?: string;
      effectiveType?: string;
      downlink?: number;
      rtt?: number;
    };
    mozConnection?: {
      type?: string;
      effectiveType?: string;
      downlink?: number;
      rtt?: number;
    };
    webkitConnection?: {
      type?: string;
      effectiveType?: string;
      downlink?: number;
      rtt?: number;
    };
  };

  const connection =
    navigatorWithConnection.connection ||
    navigatorWithConnection.mozConnection ||
    navigatorWithConnection.webkitConnection;

  return {
    type: connection?.type ?? null,
    effectiveType: connection?.effectiveType ?? null,
    downlink: connection?.downlink ?? null,
    rtt: connection?.rtt ?? null,
  };
}

/** Retrieves battery info from Battery Status API */
async function getBatteryInfo(): Promise<BatteryInfo> {
  if (typeof navigator === 'undefined') {
    return { level: null, charging: null, supported: false };
  }

  try {
    const nav = navigator as unknown as {
      getBattery?: () => Promise<{
        level: number;
        charging: boolean;
      }>;
    };

    if (nav.getBattery) {
      const battery = await nav.getBattery();
      return {
        level: battery.level * 100,
        charging: battery.charging,
        supported: true,
      };
    }
  } catch {
    // API unavailable
  }

  return { level: null, charging: null, supported: false };
}

/** Retrieves storage info from Storage API (returns GB) */
async function getStorageInfo(): Promise<StorageInfo> {
  if (typeof navigator === 'undefined') {
    return { quota: null, usage: null, supported: false };
  }

  try {
    if (navigator.storage?.estimate) {
      const estimate = await navigator.storage.estimate();
      return {
        quota: estimate.quota ? estimate.quota / (1024 * 1024 * 1024) : null,
        usage: estimate.usage ? estimate.usage / (1024 * 1024 * 1024) : null,
        supported: true,
      };
    }
  } catch {
    // API unavailable
  }

  return { quota: null, usage: null, supported: false };
}

/** Estimates display refresh rate via requestAnimationFrame (500ms sample) */
function estimateRefreshRate(): Promise<number | null> {
  if (typeof window === 'undefined' || typeof requestAnimationFrame === 'undefined') {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    let frameCount = 0;
    const startTime = performance.now();

    const checkFrame = () => {
      frameCount++;
      const elapsed = performance.now() - startTime;
      if (elapsed >= 500) {
        resolve(Math.round((frameCount * 1000) / elapsed));
      } else {
        requestAnimationFrame(checkFrame);
      }
    };

    requestAnimationFrame(checkFrame);
  });
}

/** Detects iOS devices (iPhone/iPad/iPod), handles iPadOS 13+ Mac reporting */
function isIOS(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const platform = navigator.platform?.toLowerCase() || '';
  const userAgent = navigator.userAgent?.toLowerCase() || '';
  return (
    platform.includes('iphone') ||
    platform.includes('ipad') ||
    platform.includes('ipod') ||
    userAgent.includes('iphone') ||
    userAgent.includes('ipad') ||
    userAgent.includes('ipod') ||
    (platform === 'macintel' && navigator.maxTouchPoints > 1)
  );
}

/** Detects iPhone RAM from user agent (fallback when deviceMemory API unavailable) */
function detectiPhoneRAM(): number | null {
  if (typeof navigator === 'undefined') {
    return null;
  }
  const ua = navigator.userAgent?.toLowerCase() || '';
  if (ua.includes('iphone16,1') || ua.includes('iphone16,2')) return 8;
  if (ua.includes('iphone15,4') || ua.includes('iphone15,5')) return 6;
  if (ua.includes('iphone15,2') || ua.includes('iphone15,3')) return 6;
  if (ua.includes('iphone14,7') || ua.includes('iphone14,8')) return 6;
  if (ua.includes('iphone14,2') || ua.includes('iphone14,3')) return 6;
  if (ua.includes('iphone14,4') || ua.includes('iphone14,5')) return 4;
  if (ua.includes('iphone13,3') || ua.includes('iphone13,4')) return 6;
  if (ua.includes('iphone13,1') || ua.includes('iphone13,2')) return 4;
  if (ua.includes('iphone12,3') || ua.includes('iphone12,5')) return 4;
  if (ua.includes('iphone12,1')) return 4;
  if (ua.includes('iphone11,4') || ua.includes('iphone11,6')) return 4;
  if (ua.includes('iphone11,8')) return 3;
  if (ua.includes('iphone10,3') || ua.includes('iphone10,6')) return 3;
  if (ua.includes('iphone10,2')) return 3;
  if (ua.includes('iphone10,1') || ua.includes('iphone10,4')) return 2;
  if (ua.includes('iphone9,2') || ua.includes('iphone9,4')) return 3;
  if (ua.includes('iphone9,1') || ua.includes('iphone9,3')) return 2;
  if (ua.includes('iphone')) return null;
  return null;
}

/** Estimates device memory when API unavailable (platform/user agent hints) */
function estimateMemory(platform: string | null, userAgent: string | null): number {
  const platformLower = platform?.toLowerCase() ?? '';
  const userAgentLower = userAgent?.toLowerCase() ?? '';

  if (platformLower.includes('mac') || platformLower.includes('win')) {
    return 4;
  }
  if (
    platformLower.includes('android') ||
    platformLower.includes('iphone') ||
    platformLower.includes('ipad')
  ) {
    const chromeMatch = userAgentLower.match(/chrome\/(\d+)/);
    const safariMatch = userAgentLower.match(/version\/(\d+)/);
    let version = 0;
    if (chromeMatch) {
      version = parseInt(chromeMatch[1], 10);
    } else if (safariMatch) {
      version = parseInt(safariMatch[1], 10);
    }
    return version >= 90 || (safariMatch && version >= 14) ? 4 : 2;
  }
  return 2;
}

/**
 * Aggregates device capabilities snapshot
 */
async function getDeviceCapabilities(): Promise<DeviceCapabilities> {
  const logicalCores = navigator.hardwareConcurrency ?? FALLBACK_DEFAULT_CPU_CORES;

  const platform = navigator.platform?.toLowerCase() || '';
  const userAgent = navigator.userAgent?.toLowerCase() || '';
  const vendor = navigator.vendor?.toLowerCase() || '';
  const isMac = platform.includes('mac') || userAgent.includes('mac os');
  const isApple = vendor.includes('apple') || userAgent.includes('apple');

  let physicalCores: number;
  const isAppleSiliconMac = isMac && isApple;
  if (
    isAppleSiliconMac &&
    (logicalCores === 16 || logicalCores === 12 || logicalCores === 10 || logicalCores === 8)
  ) {
    physicalCores = logicalCores; // Known Apple Silicon core counts
  } else {
    physicalCores = estimatePhysicalCores(logicalCores);
  }

  let deviceMemory: number | null = null;
  if ('deviceMemory' in navigator) {
    const memory = (navigator as unknown as { deviceMemory?: number }).deviceMemory;
    if (typeof memory === 'number' && memory > 0) {
      deviceMemory = memory;
    }
  }

  let isMemoryCapped = deviceMemory === 8;

  const isIOSDevice = isIOS();
  if (isIOSDevice && deviceMemory === null) {
    const detectedRAM = detectiPhoneRAM();
    if (detectedRAM !== null) {
      deviceMemory = detectedRAM;
      isMemoryCapped = false;
    }
  }

  const platformStr = navigator.platform ?? null;
  const userAgentStr = navigator.userAgent ?? null;
  const maxTouchPoints = navigator.maxTouchPoints ?? 0;
  const vendorStr = navigator.vendor ?? null;
  const onLine = navigator.onLine ?? false;
  const connectionInfo = getConnectionInfo();

  const estimatedMemory = deviceMemory ?? estimateMemory(platformStr, userAgentStr);
  const [battery, storage, refreshRate, slowDeviceResult] = await Promise.all([
    getBatteryInfo(),
    getStorageInfo(),
    estimateRefreshRate(),
    isSlowDeviceAsync(),
  ]);

  return {
    cpuCores: physicalCores,
    deviceMemory: deviceMemory ?? estimatedMemory,
    isMemoryCapped,
    gpu: detectGPU(),
    platform: platformStr,
    userAgent: userAgentStr,
    maxTouchPoints,
    vendor: vendorStr,
    onLine,
    connectionType: connectionInfo.type,
    connectionEffectiveType: connectionInfo.effectiveType,
    connectionDownlink: connectionInfo.downlink,
    connectionRtt: connectionInfo.rtt,
    battery,
    storage,
    refreshRate,
    slowDevice: slowDeviceResult,
  };
}

/** Runs single CPU benchmark iteration */
function runSingleBenchmark(): Promise<number | null> {
  return new Promise((resolve) => {
    try {
      const start = performance.now();
      let x = 0;
      for (let i = 0; i < deviceCapabilitiesConfig.benchmarkIterations; i++) {
        x += Math.sqrt(i);
      }
      const duration = performance.now() - start;
      void x; // Prevent dead code elimination
      resolve(duration);
    } catch (error) {
      console.error('Benchmark error:', error);
      resolve(null);
    }
  });
}

/** Warmup to trigger JIT compilation and stabilize CPU */
function warmupCPU(): Promise<void> {
  return new Promise((resolve) => {
    let x = 0;
    for (let i = 0; i < deviceCapabilitiesConfig.benchmarkWarmupIterations; i++) {
      x += Math.sqrt(i);
    }
    void x;
    setTimeout(resolve, 50);
  });
}

/** Calculates median of sorted array */
function calculateMedian(sorted: number[]): number {
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

/** Removes outliers using IQR method */
function removeOutliers(results: number[], minValid: number): number[] {
  results.sort((a, b) => a - b);
  const q1 = results[Math.floor(results.length * 0.25)];
  const q3 = results[Math.floor(results.length * 0.75)];
  const iqr = q3 - q1;
  const filtered = results.filter((r) => r >= q1 - 1.5 * iqr && r <= q3 + 1.5 * iqr);
  return filtered.length >= minValid ? filtered : results;
}

/** Calculates coefficient of variation */
function getCoefficientOfVariation(results: number[]): number {
  const mean = results.reduce((a, b) => a + b, 0) / results.length;
  const variance = results.reduce((sum, val) => sum + (val - mean) ** 2, 0) / results.length;
  const stdDev = Math.sqrt(variance);
  return mean > 0 ? stdDev / mean : 0;
}

/**
 * CPU benchmark with multi-round statistical analysis
 */
function isSlowDeviceAsync(): Promise<{ isSlow: boolean; duration: number | null }> {
  if (typeof window === 'undefined' || typeof performance === 'undefined') {
    return Promise.resolve({ isSlow: false, duration: null });
  }

  const runTests = async (): Promise<{ isSlow: boolean; duration: number | null }> => {
    try {
      await warmupCPU();

      const results: number[] = [];
      const runRound = async (round: number): Promise<void> => {
        if (round >= deviceCapabilitiesConfig.benchmarkRounds) return;
        const duration = await runSingleBenchmark();
        if (duration !== null && duration > 0) results.push(duration);
        if (round < deviceCapabilitiesConfig.benchmarkRounds - 1) {
          await new Promise((r) => setTimeout(r, 100));
          return runRound(round + 1);
        }
      };
      await runRound(0);

      if (results.length < deviceCapabilitiesConfig.minValidBenchmarkResults) {
        return { isSlow: false, duration: null };
      }

      const validResults = removeOutliers(
        results,
        deviceCapabilitiesConfig.minValidBenchmarkResults,
      );
      const stableDuration = calculateMedian(validResults);
      const cv = getCoefficientOfVariation(validResults);

      let threshold = deviceCapabilitiesConfig.slowDeviceThreshold;
      if (cv > 0.25) threshold *= 1.75;
      else if (cv > 0.15) threshold *= 1.5;

      return { isSlow: stableDuration > threshold, duration: stableDuration };
    } catch (error) {
      console.error('Benchmark error:', error);
      return { isSlow: false, duration: null };
    }
  };

  return new Promise((resolve) => {
    const windowWithIdle = window as unknown as {
      requestIdleCallback?: typeof requestIdleCallback;
    };
    if (typeof windowWithIdle.requestIdleCallback !== 'undefined') {
      windowWithIdle.requestIdleCallback(() => runTests().then(resolve), { timeout: 10000 });
    } else {
      setTimeout(() => runTests().then(resolve), 200);
    }
  });
}

/** Weak GPU patterns (software renderers, old integrated graphics, low-end mobile GPUs) */
const WEAK_GPU_PATTERNS = [
  /swiftshader/i,
  /llvmpipe/i,
  /software\s+renderer/i,
  /mesa.*software/i,
  /intel\s+gma/i,
  /intel\s+945gm/i,
  /intel\s+(hd\s*graphics|hd\s*2000|hd\s*3000|hd\s*4000|hd\s*5000)/i,
  /intel\s+hd\s*([1-4][0-9]{3}|5[0-4][0-9]{2})/i,
  /adreno\s*(2|3[0-9]{2}|4[0-3][0-9]|5[0-3][0-9])/i,
  /mali-t[0-7][0-9]{2}/i,
  /mali-g[0-2][0-9]{2}/i,
  /powervr\s*sgx/i,
  /geforce\s*(2|3|4|5|fx|6[0-5][0-9])/i,
  /radeon\s*(hd\s*[2-5]|hd\s*6[0-4][0-9]{2}|x[0-9]{3}|[0-9]{4})/i,
  /via\s+/i,
  /sis\s+/i,
];

/** Checks if GPU renderer matches weak GPU patterns */
function matchesWeakGPUPattern(renderer: string | null): boolean {
  if (!renderer) {
    return false;
  }

  const rendererLower = renderer.toLowerCase();
  return WEAK_GPU_PATTERNS.some((pattern) => pattern.test(rendererLower));
}

/** Determines if GPU is weak (checks patterns and capability limits) */
function isWeakGPU(gpu: GPUCapabilities | null): boolean {
  if (!gpu) {
    return true;
  }

  if (matchesWeakGPUPattern(gpu.renderer)) return true;
  const minTextureSize = deviceCapabilitiesConfig.minTextureSize ?? FALLBACK_MIN_TEXTURE_SIZE;
  const minVertexUniforms =
    deviceCapabilitiesConfig.minVertexUniformVectors ?? FALLBACK_MIN_VERTEX_UNIFORM_VECTORS;

  const isLowTextureSize = gpu.maxTextureSize < minTextureSize;
  const isLowVertexUniforms = gpu.maxVertexUniformVectors < minVertexUniforms;

  return isLowTextureSize || isLowVertexUniforms;
}

/**
 * Determines if device is low-end (score ≥ 2 out of 4 indicators)
 */
function isLowEndDevice(capabilities: DeviceCapabilities): boolean {
  let score = 0;

  const cores = capabilities.cpuCores || FALLBACK_DEFAULT_CPU_CORES;
  if (cores < deviceCapabilitiesConfig.lowCPUCoresThreshold) {
    score++;
  }

  const memory = capabilities.deviceMemory ?? 2;
  if (memory <= deviceCapabilitiesConfig.lowMemoryThreshold) score++;
  if (isWeakGPU(capabilities.gpu)) score++;
  if (capabilities.slowDevice.isSlow) score++;
  return score >= 2;
}

/**
 * Determines if device is high-end (all conditions must be met)
 */
function isHighEndDevice(capabilities: DeviceCapabilities, hasWeakGPU: boolean): boolean {
  const hasHighCPUCores = capabilities.cpuCores > deviceCapabilitiesConfig.highCPUCoresThreshold;
  const hasHighMemory =
    capabilities.deviceMemory !== null &&
    capabilities.deviceMemory >= deviceCapabilitiesConfig.highMemoryThreshold;
  const hasStrongGPU = !hasWeakGPU;
  const hasHighRefreshRate =
    capabilities.refreshRate !== null &&
    capabilities.refreshRate >= deviceCapabilitiesConfig.minRefreshRateForHighEnd;

  return hasHighCPUCores && hasHighMemory && hasStrongGPU && hasHighRefreshRate;
}

/**
 * Main entry point for device performance level detection
 */
export async function getDevicePerformanceLevel(): Promise<DevicePerformanceLevel> {
  try {
    const capabilities = await getDeviceCapabilities();
    const hasWeakGPU = isWeakGPU(capabilities.gpu);

    let result: DevicePerformanceLevel;
    if (isLowEndDevice(capabilities)) {
      result = DevicePerformanceLevel.LOW;
    } else if (isHighEndDevice(capabilities, hasWeakGPU)) {
      result = DevicePerformanceLevel.HIGH;
    } else {
      result = DevicePerformanceLevel.MEDIUM;
    }

    console.debug('[DeviceCapabilities]', {
      performanceLevel: result,
      capabilities,
      hasWeakGPU,
    });

    return result;
  } catch {
    return DevicePerformanceLevel.MEDIUM;
  }
}

// Export constants for synchronous use cases (not used on web, exported for consistency)
export const DEVICE_PERFORMANCE_LEVEL = DevicePerformanceLevel.HIGH;

// Legacy export
export const DevicePerformanceLevels = Object.freeze({
  HIGH: DevicePerformanceLevel.HIGH,
  LOW: DevicePerformanceLevel.LOW,
});
